import {
  AddressType,
  AgencyInformationType,
  AppDetailsResponseType,
  AppDetailsType,
  ClientFieldDataType,
  CommandType,
  DriverCommandsType,
  DriversDataType,
  DriverType,
  FieldDataType,
  FlexiblePaymentPreferencesType,
  InsurableItemType,
  InsurablePartyType,
  InsuranceProductType,
  PaymentPreferencesType,
  ServerCoveragesValidationObjectType,
  ServerErrorResponseType,
  VehicleCommandsType,
  VehiclesDataType,
} from 'V2/Types/AppDetails';
import { formatDateShort } from 'V2/Utilities/DateHelpers';
import {
  formatPhoneNumber,
  ISOMaskedRegex,
  parseFromMaskedISO,
} from 'V2/Utilities/Formatters';
import { isPresent } from 'V2/Utilities/ObjectHelpers';
import {
  combineStrings,
  toCamelCase,
  toTitleCase,
} from 'V2/Utilities/StringHelpers';

type InsuranceProductPolicyHoldersType =
  AppDetailsResponseType['insuranceApplication']['attributes']['insuranceProducts'][0]['attributes']['policyHolders'];

type ValueTransformerType<T> = (value: T) => ClientFieldDataType['value'];

class ProductToggleableError extends Error {
  productToggled: boolean;

  constructor(message: string, productToggled: boolean) {
    super(message);
    this.productToggled = productToggled;
  }
}

const DEFAULTS = {
  vehicle: {
    activeDisable: 'false',
    alarm: 'false',
    passiveDisable: 'false',
    recovery: 'false',
  },
};

export const isInsuranceApplication = (
  value: AppDetailsResponseType | ServerErrorResponseType
): value is AppDetailsResponseType => {
  return value.hasOwnProperty('insuranceApplication');
};

export const isInsuranceProductResponse = (
  value: InsuranceProductType | ServerErrorResponseType
): value is InsuranceProductType => {
  return value.hasOwnProperty('attributes');
};

export const transformAppDetails = (
  serverResponse: AppDetailsResponseType | ServerErrorResponseType
): AppDetailsType => {
  if (isInsuranceApplication(serverResponse)) {
    const autoInsuranceProduct = getAutoInsuranceProduct(serverResponse);

    return {
      drivers: transformDriverDetails(serverResponse),
      vehicles: transformVehicleDetails(serverResponse),
      contactInfo: transformContactInfo(serverResponse),
      coverages: transformCoverageDetails(serverResponse),
      vehicleDriverAssignment: transformVehicleDriverAssigments(serverResponse),
      commands: transformCommands(serverResponse),
      address: transformAddressDetails(serverResponse),
      addressOutcomes: transformAddressOutcomes(serverResponse),
      effectiveDate: transformEffectiveDate(serverResponse, 'effectiveDate'),
      terminationDate: transformEffectiveDate(
        serverResponse,
        'terminationDate'
      ),
      underwriting: transformUnderwritingDetails(serverResponse),
      quoteNumber: transformQuoteNumber(serverResponse),
      status: transformStatus(serverResponse),
      principalInsuredId:
        getPrincipalInsured(serverResponse)?.attributes.identifier ?? '',
      principalInsuredName: toTitleCase(
        combineStrings([
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.firstName.value ?? '',
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.middleName.value ?? '',
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.lastName.value ?? '',
        ])
      ),
      eligibleDiscounts:
        autoInsuranceProduct &&
        transformEligibleDiscounts(autoInsuranceProduct),
      agencyInformation:
        autoInsuranceProduct &&
        transformAgencyInformation(autoInsuranceProduct),
      documentSignatures: transformDocumentSignatures(serverResponse),
      softStops: transformSoftStops(serverResponse),
      disclosures: transformDisclosures(serverResponse),
      quoteChangeAcknowledgement:
        serverResponse.insuranceApplication.attributes
          .quoteChangeAcknowledgement,
      paymentPreferences: transformPaymentPreferences(
        serverResponse.insuranceApplication.attributes.paymentPreferences
      ),
      flexiblePaymentPreferences: transformFlexiblePaymentPreferences(
        serverResponse.insuranceApplication.attributes
          .flexiblePaymentPreferences
      ),
      configDiscoveryParams:
        serverResponse.insuranceApplication.attributes.configDiscoveryParams,
      coverageValidationErrors:
        transformCoverageValidationErrors(serverResponse),
      productToggled: serverResponse.insuranceApplication.productToggled,
      policyHolders: transformPolicyHolders(autoInsuranceProduct),
      productType: transformProductType(autoInsuranceProduct),
      telematics: transformTelematics(serverResponse),
      productSegment: transformProductSegment(autoInsuranceProduct),
      languagePreference:
        serverResponse.insuranceApplication.attributes.languagePreference,
    };
  } else {
    // server returned an error, so we throw an error and the consumer handles these errors.
    throw new ProductToggleableError(
      serverResponse.message,
      !!serverResponse.public_context?.product_toggled
    );
  }
};

const getAutoInsuranceProduct = (serverResponse: AppDetailsResponseType) =>
  serverResponse.insuranceApplication.attributes.insuranceProducts.find(
    (product: InsuranceProductType) =>
      product.attributes.productType === 'personalAuto'
  );

const transformDriverDetails = (serverResponse: AppDetailsResponseType) => {
  const insuranceProductPolicyHolders =
    getInsurableProductPolicyHolders(serverResponse);

  return serverResponse.insuranceApplication.attributes.insurableParties.reduce(
    (
      acc: DriversDataType,
      {
        attributes: {
          identifier,
          editable,
          discoveredDriver,
          accidents,
          personalInfo,
          driverLicense,
          violations,
          drivingHistory,
          drivingCourse,
          currentInsurance,
          contactInformation,
          education,
          excludeFromQuote,
          exclusionReason,
          homeOwnership,
          militaryService,
          profession,
          isGoodStudent,
          statusDetails,
          stateResidency,
        },
      }: InsurablePartyType
    ) => {
      return {
        ...acc,
        [identifier]: {
          identifier: identifier,
          editable: editable,
          accidents: accidents.reduce(
            (accidentAcc, accident) => ({
              ...accidentAcc,
              [accident.attributes.identifier]: {
                identifier: accident.attributes.identifier,
                atFault: mapToClientDataType(accident.attributes.atFault),
                day: mapToClientDataType(accident.attributes.day),
                month: mapToClientDataType(accident.attributes.month),
                year: mapToClientDataType(accident.attributes.year),
                lossType: mapToClientDataType(accident.attributes.lossType),
                accidentType: mapToClientDataType(
                  accident.attributes.accidentType
                ),
                typeOfLoss: mapToClientDataType(accident.attributes.typeOfLoss),
                isLendingLoss: accident.attributes.isLendingLoss,
                date: mapToClientDataType(accident.attributes.date),
                lossAmount: mapToClientDataType(accident.attributes.lossAmount),
                claimStatus: mapToClientDataType(
                  accident.attributes.claimStatus
                ),
              },
            }),
            {}
          ),
          ageFirstLicensed: mapToClientDataType(
            drivingHistory.attributes.ageFirstLicensed
          ),
          currentInsuranceBiLevel: mapToClientDataType(
            currentInsurance.attributes?.biLevel
          ),
          currentInsuranceCarrierName: mapToClientDataType(
            currentInsurance.attributes?.carrierName
          ),
          currentInsuranceLapseDuration: mapToClientDataType(
            currentInsurance.attributes?.lapseDuration
          ),
          currentInsuranceLapseReason: mapToClientDataType(
            currentInsurance.attributes?.lapseReason
          ),
          currentInsuranceLapseReasonOther: mapToClientDataType(
            currentInsurance.attributes?.lapseReasonOther
          ),
          currentInsuranceInsuredDuration: mapToClientDataType(
            currentInsurance.attributes?.insuredDuration
          ),
          dateOfBirth: mapToClientDataType(
            personalInfo.attributes.dateOfBirth,
            (value) => {
              if (!value) return '';
              return value.match(ISOMaskedRegex)
                ? parseFromMaskedISO(value)
                : formatDateShort(value);
            }
          ),
          discoveredDriver: discoveredDriver.value ?? false,
          driverType: getDriverType(insuranceProductPolicyHolders, identifier),
          drivingCourseDate: mapToClientDataType(
            drivingCourse.attributes.completionDate,
            (value) => (value ? formatDateShort(value) : '')
          ),
          educationLevel: mapToClientDataType(
            education.attributes.currentLevel
          ),
          email: mapToClientDataType(contactInformation.attributes.email),
          excludeFromQuote: mapToClientDataType(excludeFromQuote),
          exclusionReason: mapToClientDataType(exclusionReason),
          firstName: mapToClientDataType(personalInfo.attributes.firstName),
          gender: mapToClientDataType(personalInfo.attributes.gender),
          hasCompletedDrivingCourse: mapToClientDataType(
            drivingCourse.attributes.courseTaken
          ),
          isCurrentlyInsured: mapToClientDataType(
            currentInsurance.attributes?.insured
          ),
          isDeployed: mapToClientDataType(
            militaryService.attributes.isDeployed
          ),
          isServiceMember: mapToClientDataType(
            militaryService.attributes.serviceMember
          ),
          isHomeOwner: mapToClientDataType(homeOwnership.attributes.homeOwner),
          lastName: mapToClientDataType(personalInfo.attributes.lastName),
          licenseNumber: mapToClientDataType(driverLicense.attributes.number),
          licensePreviouslySuspended: mapToClientDataType(
            drivingHistory.attributes.licensePreviouslySuspended
          ),
          licenseState: mapToClientDataType(driverLicense.attributes.state),
          licenseStatus: mapToClientDataType(driverLicense.attributes.status),
          licenseVerified: mapToClientDataType(
            driverLicense.attributes.verified
          ),
          hasForeignLicense: mapToClientDataType(
            driverLicense.attributes.hasForeignLicense
          ),
          hasLearnersPermit: mapToClientDataType(
            driverLicense.attributes.hasLearnersPermit
          ),
          maritalStatus: mapToClientDataType(
            personalInfo.attributes.maritalStatus
          ),
          middleName: mapToClientDataType(personalInfo.attributes.middleName),
          militaryRank: mapToClientDataType(
            militaryService.attributes.militaryRank
          ),
          militaryServiceType: mapToClientDataType(
            militaryService.attributes.serviceType
          ),
          phoneNumber: mapToClientDataType(
            contactInformation.attributes.telephone.attributes.number
          ),
          phoneType: mapToClientDataType(
            contactInformation.attributes.telephone.attributes.phoneType
          ),
          profession: mapToClientDataType(profession),
          previousSuspensionCount: mapToClientDataType(
            drivingHistory.attributes.previousSuspensionCount
          ),
          previousSuspensionDate: mapToClientDataType(
            drivingHistory.attributes.previousSuspensionDate
          ),
          relationshipToApplicant: mapToClientDataType(
            personalInfo.attributes.relationshipToApplicant
          ),
          ssn: mapToClientDataType(personalInfo.attributes.ssn),
          suffix: mapToClientDataType(personalInfo.attributes.suffix),
          isGoodStudent: mapToClientDataType(isGoodStudent),
          hasAccessToVehicles: mapToClientDataType(
            statusDetails?.attributes?.hasAccessToVehicles
          ),
          livesInHousehold: mapToClientDataType(
            statusDetails?.attributes?.livesInHousehold
          ),
          hasOwnInsurancePolicy: mapToClientDataType(
            statusDetails?.attributes?.hasOwnInsurancePolicy
          ),
          violations: violations.reduce(
            (violationAcc, violation) => ({
              ...violationAcc,
              [violation.attributes.identifier]: {
                identifier: violation.attributes.identifier,
                month: mapToClientDataType(violation.attributes.month),
                day: mapToClientDataType(violation.attributes.day),
                violationType: mapToClientDataType(
                  violation.attributes.violationType
                ),
                year: mapToClientDataType(violation.attributes.year),
                sr22: mapToClientDataType(violation.attributes.sr22),
              },
            }),
            {}
          ),
          inStateResidencyDuration: mapToClientDataType(
            stateResidency.attributes.inStateResidencyDuration
          ),
          multipleHouseholdsMultipleStates: mapToClientDataType(
            stateResidency.attributes.multipleHouseholdsMultipleStates
          ),
          outOfStateStudent: mapToClientDataType(
            stateResidency.attributes.outOfStateStudent
          ),
          ownVehicleWhileOutOfStateStudent: mapToClientDataType(
            stateResidency.attributes.ownVehicleWhileOutOfStateStudent
          ),
        },
      };
    },
    {}
  );
};

const transformVehicleDetails = (serverResponse: AppDetailsResponseType) =>
  serverResponse.insuranceApplication.attributes.insurableItems.reduce(
    (acc: VehiclesDataType, vehicle: InsurableItemType) => {
      const {
        adas,
        needsAdas,
        identifier,
        editable,
        ownership,
        parkingDetails,
        parkingType,
        usageDetails,
        antiTheft,
        vehicleIdentification,
        mileage,
        usage,
        rideshareUse,
        needsGaragingAddress,
        garagingAddress,
        garagedAtPrimaryAddress,
      } = vehicle.attributes;
      return {
        ...acc,
        [identifier]: {
          adas,
          needsAdas,
          identifier,
          editable,
          activeDisable: mapToClientDataType(
            antiTheft.attributes.activeDisable,
            (value) =>
              value ? value.toString() : DEFAULTS.vehicle.activeDisable
          ),
          alarm: mapToClientDataType(antiTheft.attributes.alarm, (value) =>
            value ? value.toString() : DEFAULTS.vehicle.alarm
          ),
          annualMileage: mapToClientDataType(mileage.attributes.annual),
          bodyStyle: mapToClientDataType(
            vehicleIdentification.attributes.bodyStyle
          ),
          lienHolder: {
            name: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.name,
              undefined,
              true
            ),
            addressLine1: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .addressLine1,
              undefined,
              true
            ),
            addressLine2: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .addressLine2,
              undefined,
              true
            ),
            city: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .city,
              undefined,
              true
            ),
            state: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .state,
              undefined,
              true
            ),
            postalCode: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .postalCode,
              undefined,
              true
            ),
          },
          make: mapToClientDataType(vehicleIdentification.attributes.make),
          model: mapToClientDataType(vehicleIdentification.attributes.model),
          ownershipType: mapToClientDataType(
            ownership.attributes.ownershipType
          ),
          parkingDetails: {
            inStateParkingDuration: mapToClientDataType(
              parkingDetails.attributes.inStateParkingDuration
            ),
            parkingLocationReason: mapToClientDataType(
              parkingDetails.attributes.parkingLocationReason
            ),
          },
          parkingType: mapToClientDataType(parkingType ?? 'unknown'),
          usedInMexico: mapToClientDataType(
            usageDetails.attributes.usedInMexico,
            convertToString
          ),
          availableForNonCoveredDrivers: mapToClientDataType(
            usageDetails.attributes.availableForNonCoveredDrivers,
            convertToString
          ),
          emergencyServices: mapToClientDataType(
            usageDetails.attributes.emergencyServices,
            convertToString
          ),
          feeBasedServices: mapToClientDataType(
            usageDetails.attributes.feeBasedServices,
            convertToString
          ),
          hazmatToolTransport: mapToClientDataType(
            usageDetails.attributes.hazmatToolTransport,
            convertToString
          ),
          usedAsResidence: mapToClientDataType(
            usageDetails.attributes.usedAsResidence,
            convertToString
          ),
          passiveDisable: mapToClientDataType(
            antiTheft.attributes.passiveDisable,
            (value) =>
              value ? value.toString() : DEFAULTS.vehicle.passiveDisable
          ),
          recovery: mapToClientDataType(
            antiTheft.attributes.recovery,
            (value) => (value ? value.toString() : DEFAULTS.vehicle.recovery)
          ),
          rideshareUse: mapToClientDataType(rideshareUse),
          registeredToApplicant: mapToClientDataType(
            ownership.attributes.registeredToApplicant
          ),
          totalMileage: mapToClientDataType(mileage.attributes.total),
          usage: mapToClientDataType(usage),
          vehicleServicesId: mapToClientDataType(
            vehicleIdentification.attributes.vehicleServicesId
          ),
          partialVin: mapToClientDataType(
            vehicleIdentification.attributes.partialVin
          ),
          vin: mapToClientDataType(vehicleIdentification.attributes.vin),
          year: mapToClientDataType(vehicleIdentification.attributes.year),
          needsGaragingAddress,
          garagingAddress: {
            addressLine1: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.addressLine1,
              undefined,
              true
            ),
            addressLine2: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.addressLine2,
              undefined,
              true
            ),
            city: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.city,
              undefined,
              true
            ),
            state: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.state,
              undefined,
              true
            ),
            postalCode: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.postalCode,
              undefined,
              true
            ),
            garagingAddressOutcome: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes
                ?.addressValidationOutcome,
              undefined,
              true
            ),
          },
          garagingAddressValidation:
            transformGaragingAddressOutcomes(garagingAddress),
          garagedAtPrimaryAddress: mapToClientDataType(garagedAtPrimaryAddress),
        },
      };
    },
    {}
  );

const allowedOutcomes = ['mailable', 'unavailable', 'vacant', 'overridden'];

export const transformGaragingAddressOutcomes = (
  garagingAddress: InsurableItemType['attributes']['garagingAddress']
) => {
  const garagingAddressOutcome =
    garagingAddress.attributes.address.attributes.addressValidationOutcome;

  const garagingAddressLine1 =
    garagingAddress.attributes.address.attributes.addressLine1;

  const outcome = garagingAddressOutcome?.value || null;
  const hasOutcomeErrors = garagingAddressOutcome?.errors.length > 0;

  const isAddressValid = allowedOutcomes.includes(outcome || '');
  const isMissingLineTwo = outcome === 'missing_line_two';

  if (
    outcome === null &&
    (!hasOutcomeErrors || garagingAddressLine1.value === null)
  ) {
    // We don't have an address or an outcome yet, so don't show any error
    return null;
  }

  return {
    isAddressValid,
    isMissingLineTwo,
  };
};

export const transformContactInfo = (
  serverResponse: AppDetailsResponseType
) => {
  const principalInsured = getPrincipalInsured(serverResponse);
  const contactInformation =
    principalInsured!.attributes.contactInformation.attributes;
  return {
    [principalInsured!.attributes.identifier]: {
      email: mapToClientDataType(contactInformation.email),
      phoneNumber: mapToClientDataType(
        contactInformation.telephone.attributes.number,
        (value) => (value ? formatPhoneNumber(value) : '')
      ),
      phoneType: mapToClientDataType(
        contactInformation.telephone.attributes.phoneType
      ),
    },
  };
};

const transformCoverageDetails = (serverResponse: AppDetailsResponseType) => {
  const insuranceProduct =
    serverResponse.insuranceApplication.attributes.insuranceProducts.find(
      (product: InsuranceProductType) =>
        product.attributes.productType === 'personalAuto'
    );

  return transformCoverages(insuranceProduct);
};

export const transformCoverages = (
  serverResponse?: InsuranceProductType | ServerErrorResponseType | undefined
) => {
  if (!serverResponse || isInsuranceProductResponse(serverResponse)) {
    const policyCoverages =
      serverResponse?.attributes.coverages.insuranceProduct ?? [];
    const vehicleCoverages =
      serverResponse?.attributes.coverages.insurableItem ?? [];

    return {
      policy: policyCoverages.reduce<
        Record<string, Required<ClientFieldDataType>>
      >((acc, c) => {
        const fields = mapToClientDataType(c.attributes.coverageLevelEnum);
        const errors = [
          // ...fields.errors, // currently we only get errors outside the field
          ...(c?.errors || []),
        ];
        return {
          ...acc,
          [c.attributes.coverageType]: {
            ...fields,
            errors,
          },
        };
      }, {}),
      vehicles: vehicleCoverages.reduce<
        Record<string, Record<string, Required<ClientFieldDataType>>>
      >((acc, c) => {
        const fields = mapToClientDataType(c.attributes.coverageLevelEnum);
        const errors = [
          // ...fields.errors, // currently we only get errors outside the field
          ...(c?.errors || []),
        ];
        return {
          ...acc,
          [c.attributes.insurableItemIdentifier]: {
            ...(acc[c.attributes.insurableItemIdentifier] ?? {}),
            [c.attributes.coverageType]: {
              ...fields,
              errors,
            },
          },
        };
      }, {}),
    };
  } else {
    // server returned an error, so we throw an error and the consumer handles these errors.
    throw new Error(serverResponse.message);
  }
};

export const transformVehicleDriverAssigments = (
  serverResponse: AppDetailsResponseType
) => {
  const { insurableItems, insurableParties } =
    serverResponse.insuranceApplication.attributes;
  const address = transformAddressDetails(serverResponse);
  const insuranceProduct = getAutoInsuranceProduct(serverResponse);
  const principalInsured = getPrincipalInsured(serverResponse);

  if (
    address[principalInsured!.attributes.identifier].state.value !== 'VA' ||
    insurableItems.length <= 1 ||
    insurableParties.length <= 1
  ) {
    return null;
  }

  return insuranceProduct!.attributes.insurableItemParties.elements.reduce(
    (acc, element) => ({
      ...acc,
      [element.insurableItem]: element.insurableParties,
    }),
    {}
  );
};

const transformAddressDetails = (serverResponse: AppDetailsResponseType) => {
  const primaryAddress =
    serverResponse.insuranceApplication.attributes.addresses[0];
  const principalInsured = getPrincipalInsured(serverResponse);
  return {
    [principalInsured!.attributes.identifier]: {
      identifier: primaryAddress.attributes.identifier,
      addressValidationOutcome: mapToClientDataType(
        primaryAddress.attributes.addressValidationOutcome
      ),
      address1: mapToClientDataType(primaryAddress.attributes.addressLine1),
      address2: mapToClientDataType(primaryAddress.attributes.addressLine2),
      city: mapToClientDataType(primaryAddress.attributes.city),
      state: mapToClientDataType(primaryAddress.attributes.state),
      zip: mapToClientDataType(primaryAddress.attributes.postalCode),
    },
  };
};

const transformCommands = (serverResponse: AppDetailsResponseType) => {
  const bindCommand = serverResponse.insuranceApplication.commands.find(
    (c) => c.name === 'bind'
  );

  const sendSignaturePacketCommand =
    serverResponse.insuranceApplication.commands.find(
      (c) => c.name === 'send_signature_packet'
    );

  const autoInsuranceProduct = getAutoInsuranceProduct(serverResponse);

  const rateCommand = serverResponse.insuranceApplication.commands.find(
    (c) => c.name === 'rate'
  );
  return {
    reports: serverResponse.insuranceApplication.attributes.reports.map(
      (c) => ({
        reportName: c.reportEnum,
        runnable: c.command.runnable,
        status: c.command.status,
      })
    ),
    rate: transformCommand(rateCommand),
    bind: transformCommand(bindCommand),
    sendSignaturePacket: sendSignaturePacketCommand
      ? transformCommand(sendSignaturePacketCommand)
      : undefined,
    addresses: serverResponse.insuranceApplication.attributes.addresses.reduce(
      (acc: AddressType['commands'], address) => [...acc, ...address.commands],
      []
    ),
    insuranceProduct: autoInsuranceProduct?.commands || [],
    drivers:
      serverResponse.insuranceApplication.attributes.insurableParties.reduce(
        (acc: DriverCommandsType, driver) => {
          return {
            ...acc,
            [driver.attributes.identifier]: {
              commands: driver.commands ?? driver.attributes.commands,
              challenging: {
                currentInsurance: driver.attributes.currentInsurance.commands,
                driverLicense: driver.attributes.driverLicense.commands,
              },
              accidents: driver.attributes.accidents.reduce(
                (acc: DriverCommandsType[0]['accidents'], accident) => {
                  return {
                    ...acc,
                    [accident.attributes.identifier]: accident.commands,
                  };
                },
                {}
              ),
              violations: driver.attributes.violations.reduce(
                (acc: DriverCommandsType[0]['violations'], violation) => {
                  return {
                    ...acc,
                    [violation.attributes.identifier]: violation.commands,
                  };
                },
                {}
              ),
            },
          };
        },
        {}
      ),
    vehicles:
      serverResponse.insuranceApplication.attributes.insurableItems.reduce(
        (acc: VehicleCommandsType, vehicle) => {
          return {
            ...acc,
            [vehicle.attributes.identifier]: { commands: vehicle.commands },
          };
        },
        {}
      ),
  };
};

const transformCommand = (
  command: CommandType | undefined
): Partial<CommandType> => ({
  status: command?.status || 'not_runnable',
  runnable: isPresent(command) ? command.runnable : false,
});

export const transformAddressOutcomes = (response: AppDetailsResponseType) => {
  const addressIdentifier = response.insuranceApplication.attributes.identifier;
  const address = response.insuranceApplication.attributes.addresses.find(
    (a) => a.attributes.identifier === addressIdentifier
  );
  const outcome = isPresent(address)
    ? address.attributes.addressValidationOutcome.value || ''
    : '';
  const isAddressValid = allowedOutcomes.includes(outcome);
  const isMissingLineTwo = outcome === 'missing_line_two';
  return {
    isAddressValid,
    isMissingLineTwo,
  };
};

export const transformEffectiveDate = (
  serverResponse: AppDetailsResponseType,
  attribureName: 'effectiveDate' | 'terminationDate'
) => {
  const insuranceProduct =
    serverResponse.insuranceApplication.attributes.insuranceProducts.find(
      (product: InsuranceProductType) =>
        product.attributes.productType === 'personalAuto'
    );

  return mapToClientDataType(
    insuranceProduct?.attributes.policyTerm.attributes[attribureName],
    (value) => (value ? formatDateShort(value) : '')
  );
};

const transformUnderwritingDetails = (
  serverResponse: AppDetailsResponseType
) => {
  return {
    failureReasons:
      serverResponse.insuranceApplication.attributes.underwriting
        .failureReasons ?? [],
    underwritingStops:
      serverResponse.insuranceApplication.attributes.underwriting
        .underwritingStops ?? [],
    underwritingPassed:
      serverResponse.insuranceApplication.attributes.underwriting
        .underwritingPassed ?? true, // if undefined, this can default to true since there is simply no decision supplied yet
  };
};

export const transformQuoteNumber = (
  serverResponse: AppDetailsResponseType
): string => serverResponse.insuranceApplication.attributes.quoteNumber;

export const transformStatus = (serverResponse: AppDetailsResponseType) => {
  return serverResponse.insuranceApplication.attributes.status;
};

export const transformEligibleDiscounts = (
  autoInsuranceProduct: InsuranceProductType
) => {
  return autoInsuranceProduct?.attributes.eligibleDiscounts || [];
};

export const transformAgencyInformation = (
  autoInsuranceProduct: InsuranceProductType
): AgencyInformationType => {
  const agencyInformation = {
    ...autoInsuranceProduct.attributes.agencyInformation,
  };
  return agencyInformation;
};

const transformDocumentSignatures = (
  serverResponse: AppDetailsResponseType
) => {
  return (
    serverResponse.insuranceApplication.attributes.documentSignatures || []
  );
};

const transformSoftStops = (serverResponse: AppDetailsResponseType) => {
  return serverResponse.insuranceApplication.attributes.softStops ?? [];
};

const transformDisclosures = (serverResponse: AppDetailsResponseType) => {
  return serverResponse.insuranceApplication.attributes.disclosures || [];
};

const transformCoverageValidationErrors = (
  serverResponse: AppDetailsResponseType
) => {
  const { coverageSchemaValidationErrors } =
    serverResponse.insuranceApplication.attributes;

  if (!coverageSchemaValidationErrors) {
    return null;
  }

  try {
    const policyCoverageErrors = flattenCoverageErrors(
      'policy',
      transformCoverageErrorObjectToMessages(
        coverageSchemaValidationErrors.policy?.coverages
      )
    );
    const policyUnderwritingErrors = Object.values(
      coverageSchemaValidationErrors.policy?.underwriting_rules || {}
    ).map((uwErrors) =>
      flattenCoverageErrors(
        'policy',
        transformCoverageErrorObjectToMessages(uwErrors.coverages)
      )
    );
    const vehiclesErrors = Object.entries(
      coverageSchemaValidationErrors.vehicles || {}
    ).flatMap(([index, vehicleErrors]) => {
      const vehicleId =
        serverResponse.insuranceApplication.attributes.insurableItems[+index]
          .attributes.identifier;

      const vehicleUnderwritingErrors = Object.values(
        vehicleErrors.underwriting_rules || {}
      ).map((uwErrors) =>
        flattenCoverageErrors(
          `vehicles.${vehicleId}`,
          transformCoverageErrorObjectToMessages(uwErrors.coverages)
        )
      );
      return [
        flattenCoverageErrors(
          `vehicles.${vehicleId}`,
          transformCoverageErrorObjectToMessages(vehicleErrors?.coverages)
        ),
        ...vehicleUnderwritingErrors,
      ];
    });

    return Object.assign(
      policyCoverageErrors,
      ...policyUnderwritingErrors,
      ...vehiclesErrors
    );
  } catch (error) {
    console.error(
      'Error transforming coverage errors',
      coverageSchemaValidationErrors,
      error
    );

    return null;
  }
};

const transformCoverageErrorObjectToMessages = (
  errors: ServerCoveragesValidationObjectType | undefined
) => {
  if (!errors) return {};

  return Object.keys(errors).reduce((validationErrors, coverage) => {
    return {
      ...validationErrors,
      [coverage]: 'Please select a valid coverage option',
    };
  }, {});
};

export const transformPolicyHolders = (
  autoInsuranceProduct?: InsuranceProductType
) => {
  return autoInsuranceProduct?.attributes.policyHolders || [];
};

export const transformProductType = (
  autoInsuranceProduct?: InsuranceProductType
) => autoInsuranceProduct?.attributes.productType;

export const transformTelematics = (
  seerverResponse: AppDetailsResponseType
) => {
  const insuranceProduct =
    seerverResponse.insuranceApplication.attributes.insuranceProducts.find(
      (product: InsuranceProductType) =>
        product.attributes.productType === 'personalAuto'
    );

  return insuranceProduct?.attributes.telematics;
};

export const transformProductSegment = (
  autoInsuranceProduct?: InsuranceProductType
) => autoInsuranceProduct?.attributes.productSegment;

export const transformPaymentPreferences = (
  paymentPreferences: PaymentPreferencesType
) => {
  return {
    frequency: toCamelCase(paymentPreferences?.frequency),
    method: paymentPreferences?.method,
    paymentPlanEnum: paymentPreferences?.paymentPlanEnum,
  };
};

export const transformFlexiblePaymentPreferences = (
  flexiblePaymentPreferences: FlexiblePaymentPreferencesType
) => {
  return {
    frequency: toCamelCase(flexiblePaymentPreferences?.frequency),
    daysOfMonth: flexiblePaymentPreferences?.daysOfMonth,
    daysOfWeek: flexiblePaymentPreferences?.daysOfWeek,
  };
};

const getInsurableProductPolicyHolders = (
  serverResponse: AppDetailsResponseType
) => {
  return (
    getAutoInsuranceProduct(serverResponse)?.attributes.policyHolders ?? []
  );
};

export const getPrincipalInsured = (serverResponse: AppDetailsResponseType) => {
  const policyHolders = getInsurableProductPolicyHolders(serverResponse);
  return serverResponse.insuranceApplication.attributes.insurableParties.find(
    (driver) =>
      getDriverType(policyHolders, driver.attributes.identifier) ===
      'principalInsured'
  );
};

const getDriverType = (
  insuranceProductPolicyHolders: InsuranceProductPolicyHoldersType,
  driverId: string
): DriverType => {
  return (
    insuranceProductPolicyHolders.find(
      (policyHolder) => policyHolder.identifier === driverId
    )?.policyHolderType.value ?? 'principalInsured'
  );
};

const mapToClientDataType = function <T extends Object>(
  fieldData: FieldDataType<T> | undefined,
  valueTransformer:
    | ValueTransformerType<FieldDataType<T>['value']>
    | undefined = undefined,
  editableIfEmpty = false
): ClientFieldDataType {
  if (!fieldData) {
    return {
      value: '',
      errors: [],
      source: '',
      editable: editableIfEmpty,
      masked: false,
    };
  }

  let value;

  if (valueTransformer) {
    value = valueTransformer(fieldData.value);
  } else if (
    typeof fieldData.value !== 'undefined' &&
    fieldData.value !== null
  ) {
    value = fieldData.value.toString();
  } else {
    value = '';
  }

  return {
    value,
    errors: fieldData.errors,
    source: fieldData.source?.toString() ?? '',
    editable: fieldData.editable,
    masked: fieldData.masked,
  };
};

export const convertToString = (value: boolean | null): string => {
  return value !== null ? value.toString() : '';
};

const flattenCoverageErrors = (
  prefix: string,
  errors: ServerCoveragesValidationObjectType
) => {
  return Object.entries(errors).reduce((result, [coverage, error]) => {
    return { ...result, [`coverages.${prefix}.${coverage}`]: error };
  }, {});
};
